import { jsxs, jsx } from "react/jsx-runtime";
import React__default, { useState } from "react";
import classNames from "classnames";
import { FormOnColor, FormSize, AnalyticsId } from "./constants.js";
import { useUuid } from "./hooks/useUuid.js";
import { isComponent } from "./utils/component.js";
import { C as Checkbox } from "./Checkbox.js";
import { E as ErrorWrapper } from "./ErrorWrapper.js";
import { F as FormLayout } from "./FormLayout.js";
import { I as Input } from "./Input.js";
import { R as RadioButton, g as getRadioLabelClasses } from "./RadioButton.js";
import { S as Select } from "./Select.js";
import { S as Slider } from "./Slider.js";
import { T as Textarea } from "./Textarea.js";
import { T as Title } from "./Title.js";
import formGroupStyles from "./components/FormGroup/styles.module.scss";
const FormGroup = React__default.forwardRef((props, ref) => {
  const {
    className,
    fieldsetClassName,
    onColor = FormOnColor.onwhite,
    size = FormSize.medium,
    error,
    errorTextId,
    name,
    htmlMarkup = "fieldset",
    renderError = true,
    errorWrapperClassName,
    errorWrapperTestId
  } = props;
  const [checkedRadioId, setCheckedRadioId] = useState();
  const radioGroupId = useUuid();
  const errorTextUuid = useUuid(errorTextId);
  const onDark = onColor === FormOnColor.ondark;
  const isLarge = size === FormSize.large;
  const formGroupWrapperClasses = classNames(formGroupStyles["form-group-wrapper"], className);
  const titleClasses = classNames({
    [formGroupStyles["form-group-wrapper__title--on-dark"]]: onDark && !error
  });
  const legendClasses = classNames(formGroupStyles["field-set__legend"], {
    [formGroupStyles["field-set__legend--on-dark"]]: onDark && !error
  });
  const fieldsetClasses = classNames(formGroupStyles["field-set"], fieldsetClassName);
  const mapFormComponent = (child, index) => {
    if (isComponent(child, FormLayout)) {
      return React__default.cloneElement(child, {
        size,
        mapHelper: mapFormComponent
      });
    } else if (isComponent(child, FormGroup)) {
      return React__default.cloneElement(child, {
        onColor,
        size,
        error,
        renderError: false,
        errorTextId: errorTextUuid
      });
    } else if (isComponent(child, Checkbox)) {
      return React__default.cloneElement(child, {
        name: name ?? child.props.name,
        onColor,
        size,
        error: !!error,
        errorTextId: errorTextUuid
      });
    } else if (isComponent(child, RadioButton)) {
      const radioId = typeof child.props.inputId === "undefined" ? radioGroupId + index : child.props.inputId;
      return React__default.cloneElement(child, {
        inputId: radioId,
        name: name ?? child.props.name,
        onColor,
        size,
        onChange: (event) => {
          setCheckedRadioId(event.target.id);
          child.props.onChange && child.props.onChange(event);
        },
        error: !!error,
        errorTextId: errorTextUuid,
        labelClassNames: getRadioLabelClasses(radioId, onColor, isLarge, checkedRadioId)
      });
    } else if (isComponent(child, Input)) {
      return React__default.cloneElement(child, {
        name: name ?? child.props.name,
        onColor,
        size,
        error: !!error,
        errorTextId: errorTextUuid
      });
    } else if (isComponent(child, Textarea)) {
      return React__default.cloneElement(child, {
        name: name ?? child.props.name,
        onColor,
        error: !!error,
        errorTextId: errorTextUuid
      });
    } else if (isComponent(child, Select)) {
      return React__default.cloneElement(child, {
        name: name ?? child.props.name,
        onColor,
        error: !!error,
        errorTextId: errorTextUuid
      });
    } else if (isComponent(child, Slider)) {
      return React__default.cloneElement(child, {
        name: name ?? child.props.name,
        error: !!error,
        errorTextId: errorTextUuid
      });
    }
    return child;
  };
  const formGroupContent = () => {
    return /* @__PURE__ */ jsxs("div", { children: [
      htmlMarkup === "div" && /* @__PURE__ */ jsxs("div", { className: fieldsetClasses, children: [
        props.legend && /* @__PURE__ */ jsx("h5", { className: legendClasses, children: props.legend }),
        React__default.Children.map(props.children, mapFormComponent)
      ] }),
      htmlMarkup === "fieldset" && /* @__PURE__ */ jsxs("fieldset", { name: props.fieldsetName, className: fieldsetClasses, children: [
        props.legend && /* @__PURE__ */ jsx("legend", { className: legendClasses, children: props.legend }),
        React__default.Children.map(props.children, mapFormComponent)
      ] })
    ] });
  };
  return /* @__PURE__ */ jsxs("div", { "data-testid": props.testId, "data-analyticsid": AnalyticsId.FormGroup, className: formGroupWrapperClasses, ref, tabIndex: -1, children: [
    props.title && /* @__PURE__ */ jsx(Title, { className: titleClasses, htmlMarkup: "h4", appearance: "title4", margin: { marginTop: 0, marginBottom: error ? 1 : 2 }, children: props.title }),
    renderError ? /* @__PURE__ */ jsx(ErrorWrapper, { className: errorWrapperClassName, errorText: error, testId: errorWrapperTestId, errorTextId: errorTextUuid, children: formGroupContent() }) : formGroupContent()
  ] });
});
FormGroup.displayName = "FormGroup";
export {
  FormGroup as F
};
//# sourceMappingURL=FormGroup.js.map
